import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight, Quote, Calendar, Clock, TrendingUp, MapPin, Award, BadgeCheck,
  Sparkles, Target, Users, Heart, Zap, MessageCircle, Mail, Phone, Send, ArrowLeft, ShieldCheck, Scale,
} from "lucide-react";
import { PublicHeader, PublicFooter, FloatingWhatsApp, PublicLayoutStyles } from "@/components/PublicLayout";

const TEAM_PHOTO = "https://customer-assets.emergentagent.com/job_26d1aa6e-a958-43ca-9116-14748c74a37b/artifacts/jhf8ykbf_1000531957.jpg";
const FOUNDER_PHOTO = "https://customer-assets.emergentagent.com/job_26d1aa6e-a958-43ca-9116-14748c74a37b/artifacts/m9z8c7w3_1000524066.jpg";
const AMOAKON_PHOTO = "https://customer-assets.emergentagent.com/job_26d1aa6e-a958-43ca-9116-14748c74a37b/artifacts/0zialkm2_1000524127.jpg";
const LOBA_PHOTO = "https://customer-assets.emergentagent.com/job_26d1aa6e-a958-43ca-9116-14748c74a37b/artifacts/ra4bfyg1_1000524096.jpg";

const Shell = ({ children, active, title, desc, canonical }) => (
  <div className="cmes-landing min-h-screen bg-[#F9F8F6] antialiased" style={{ fontFamily: "'Manrope', sans-serif" }}>
    <Helmet
      title={String(title || "")}
      meta={[
        { name: "description", content: String(desc || "") },
      ]}
      link={[
        { rel: "canonical", href: `https://cmespharmaintel.com${canonical || ""}` },
      ]}
    />

    <PublicLayoutStyles />
    <PublicHeader activeKey={active} />
    <main>{children}</main>
    <PublicFooter />
    <FloatingWhatsApp />
  </div>
);

// ═══════════════════════════════════════════════════════════════
// 1. INSTITUTION PAGE
// ═══════════════════════════════════════════════════════════════

const FOUNDING_VALUES = [
  { icon: Heart, title: "Dévouement", desc: "Nous croyons que chaque pharmacien mérite des outils à la hauteur de son engagement quotidien envers ses patients." },
  { icon: Scale, title: "Éthique", desc: "Le Serment de Galien guide notre éthique : rigueur, intégrité, protection du secret médical. Dans chaque ligne de code." },
  { icon: Target, title: "Précision", desc: "En pharmacie, l'erreur coûte cher — en argent, en santé, en confiance. Nous visons la précision absolue." },
  { icon: Users, title: "Proximité", desc: "Nous ne sommes pas une multinationale lointaine. Nous sommes vos voisins, formés aux réalités ivoiriennes." },
];

const TIMELINE = [
  { date: "2022", title: "Genèse à Abidjan", desc: "Naissance de CMES SARL autour d'une conviction : la pharmacie africaine mérite des outils technologiques à la hauteur de son rôle sanitaire." },
  { date: "2024", title: "Premier prototype", desc: "Lancement du module PHARMA ASSURANCES avec 10 officines pilotes d'Abidjan. Premiers retours terrain structurants." },
  { date: "2025", title: "Plateforme complète", desc: "Déploiement des 3 modules : ASSURANCES, PÉRIMÉS, DISPENSATION. Base pharmaceutique étendue à 96 DCI et 119 interactions." },
  { date: "2026", title: "L'institution", desc: "CMES Pharma Intel devient le partenaire stratégique de référence. Extension pays et Afrique de l'Ouest francophone." },
];

export function InstitutionPage() {
  return (
    <Shell active="Institution" title="Notre Institution | CMES Pharma Intel — Abidjan, Côte d’Ivoire"
      desc="Derrière CMES Pharma Intel : une équipe ivoirienne passionnée, un dirigeant visionnaire, une mission au service des pharmaciens d'Afrique francophone."
      canonical="/institution">

      {/* HERO */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 bg-[#F9F8F6] overflow-hidden" data-testid="institution-hero">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Notre institution</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="font-serif text-5xl sm:text-6xl lg:text-[92px] leading-[0.95] tracking-[-0.02em] text-[#0A0A0A] mb-8 max-w-5xl">
            Une équipe ivoirienne, <br /><em className="italic font-light text-[#025946]">une ambition panafricaine.</em>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="max-w-2xl text-[17px] lg:text-[19px] leading-relaxed text-[#4A4D4C] font-light">
            Nous ne vendons pas un logiciel. Nous construisons <span className="text-[#0A0A0A] font-normal">une institution au service de la pharmacie africaine</span> — pensée par des ingénieurs, validée par des pharmaciens, ancrée à Abidjan.
          </motion.p>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="relative bg-white py-24 lg:py-32 border-y border-[#E5E4E2]" data-testid="founder-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-12 gap-8 lg:gap-16 items-center">
            <div className="col-span-12 lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-16 h-16 border-l-2 border-t-2 border-[#025946]"></div>
                <div className="absolute -bottom-4 -right-4 w-16 h-16 border-r-2 border-b-2 border-[#C9A25E]"></div>
                <img src={FOUNDER_PHOTO} alt="Dirigeant CMES Pharma Intel" className="w-full h-auto aspect-[3/4] object-cover grayscale" loading="lazy" />
                <div className="absolute bottom-4 left-4 bg-[#0A0A0A]/90 backdrop-blur-sm text-white px-4 py-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E] font-bold">Fondateur & Dirigeant</p>
                </div>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-7">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Le Fondateur</p>
              <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A] mb-8">
                Une vision, <em className="italic font-light text-[#025946]">née du terrain</em>.
              </h2>
              <div className="space-y-5 text-[15px] text-[#4A4D4C] leading-[1.8] font-light max-w-2xl">
                <p>
                  Entrepreneur ivoirien convaincu que <strong className="text-[#0A0A0A] font-medium">la transformation digitale de la santé passe par les officines</strong>, le fondateur de CMES a patiemment construit l'écosystème qui manquait aux pharmaciens d'Afrique francophone.
                </p>
                <p>
                  Issu du monde de l'innovation sanitaire et de la gestion des déchets médicaux (CMES SARL), il a rassemblé autour de lui <strong className="text-[#0A0A0A] font-medium">une équipe multidisciplinaire</strong> — ingénieurs logiciels, pharmaciens-conseils, designers — pour bâtir une plateforme qui ne parle pas aux directeurs IT, <em className="text-[#025946]">mais aux pharmaciens titulaires</em>.
                </p>
                <p>
                  Sa conviction : <em className="text-[#0A0A0A]">« Un pharmacien qui passe moins de temps en paperasse passe plus de temps avec ses patients. Et c'est cela, au fond, qui sauve des vies. »</em>
                </p>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-6 pt-8 border-t border-[#E5E4E2]">
                <div><p className="font-serif text-3xl lg:text-4xl font-light text-[#025946] mb-1">+15</p><p className="text-[11px] uppercase tracking-wider text-[#4A4D4C]">Années d'entrepreneuriat santé</p></div>
                <div><p className="font-serif text-3xl lg:text-4xl font-light text-[#025946] mb-1">50+</p><p className="text-[11px] uppercase tracking-wider text-[#4A4D4C]">Partenaires officinaux</p></div>
                <div><p className="font-serif text-3xl lg:text-4xl font-light text-[#025946] mb-1">CMES</p><p className="text-[11px] uppercase tracking-wider text-[#4A4D4C]">SARL · Groupe ivoirien</p></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="relative bg-[#F9F8F6] py-24 lg:py-32" data-testid="team-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-12 gap-6 mb-12">
            <div className="col-span-12 lg:col-span-5">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── L'équipe CMES</p>
              <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A]">
                Bâtisseurs <em className="italic font-light">au quotidien.</em>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-[15px] text-[#4A4D4C] leading-relaxed font-light">
                Derrière chaque fonctionnalité, des visages. Derrière chaque ligne de code, une mission. Notre équipe d'ingénieurs, consultants et formateurs terrain incarne <strong className="text-[#0A0A0A] font-medium">l'excellence pharmaceutique ivoirienne</strong>.
              </p>
            </div>
          </div>
          <div className="relative group">
            <img src={TEAM_PHOTO} alt="Équipe CMES Pharma Intel devant le Serment de Galien" className="w-full h-auto object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 via-transparent to-transparent"></div>
            <div className="absolute bottom-6 lg:bottom-10 left-6 lg:left-10 right-6 lg:right-10 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
              <div className="text-white">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E] mb-2">Équipe CMES · Abidjan</p>
                <p className="font-serif text-2xl lg:text-4xl font-light max-w-xl leading-tight">« Sous le regard du Serment de Galien — notre boussole éthique. »</p>
              </div>
              <div className="bg-[#C9A25E] text-[#0A0A0A] px-4 py-2 text-[10px] font-bold uppercase tracking-wider"><Scale className="w-3 h-3 inline mr-1" /> Éthique pharmaceutique</div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="relative bg-[#0A0A0A] text-[#F9F8F6] py-24 lg:py-32" data-testid="values-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E] mb-6">─── Nos valeurs fondatrices</p>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
              Ce qui nous <em className="italic font-light text-[#C9A25E]">anime.</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {FOUNDING_VALUES.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-[#0A0A0A] p-8 lg:p-10" data-testid={`value-${i}`}>
                <v.icon className="w-7 h-7 text-[#C9A25E] mb-6" strokeWidth={1.2} />
                <h3 className="font-serif text-2xl font-light mb-3">{v.title}</h3>
                <p className="text-[13px] text-white/60 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="relative bg-[#F9F8F6] py-24 lg:py-32" data-testid="timeline-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Notre chemin</p>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A]">
              Quatre années. <em className="italic font-light">Une institution.</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#E5E4E2]">
            {TIMELINE.map((t, i) => (
              <div key={t.date} className="bg-white p-8" data-testid={`timeline-${i}`}>
                <p className="font-serif text-5xl lg:text-6xl font-light text-[#025946] mb-4">{t.date}</p>
                <h3 className="text-lg font-semibold text-[#0A0A0A] mb-3">{t.title}</h3>
                <p className="text-[13px] text-[#4A4D4C] leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </Shell>
  );
}

// ═══════════════════════════════════════════════════════════════
// 2. CASE STUDIES PAGE
// ═══════════════════════════════════════════════════════════════

const CASES = [
  {
    id: "amoakon-saint-etienne",
    pharmacist: "Dr. AMOAKON",
    officine: "Pharmacie Saint-Étienne",
    city: "Yopougon, Abidjan",
    photo: AMOAKON_PHOTO,
    module: "PHARMA ASSURANCES",
    title: "De 3 semaines à 48 heures : la révolution du recouvrement.",
    summary: "Une officine de quartier qui libère sa trésorerie en scannant ses bordereaux.",
    kpis: [
      { value: "−92%", label: "Délai de recouvrement", detail: "de 21 jours à 48h" },
      { value: "+18M", label: "FCFA débloqués", detail: "en 3 mois" },
      { value: "100%", label: "Bordereaux tracés", detail: "contre 40% auparavant" },
    ],
    problem: "La Pharmacie Saint-Étienne traitait chaque mois ~180 bordereaux CMU/CNAM. Les délais de paiement des assureurs dépassaient fréquemment 3 semaines. Les relances étaient manuelles, les impayés fréquents, et la trésorerie mensuelle oscillait dangereusement en milieu de mois.",
    solution: "Déploiement de PHARMA ASSURANCES en mars 2026. Chaque bordereau est désormais photographié lors du dépôt chez l'assureur (cachet obligatoire), l'OCR extrait instantanément les informations, et le pipeline de suivi passe automatiquement du statut « Déposé » à « Payé ».",
    results: "Dès le 2ème mois d'utilisation, le délai moyen de recouvrement est passé à moins de 48 heures pour les assureurs partenaires (grâce aux relances auto-programmées). L'officine a débloqué en trois mois l'équivalent de 18 millions de FCFA de créances anciennes.",
    quote: "En 3 mois, nos bordereaux passent en 48 h au lieu de 3 semaines. La trésorerie de l'officine a changé de visage. Mon équipe passe désormais son temps avec les patients, plus avec des classeurs.",
  },
  {
    id: "loba-magnificat",
    pharmacist: "Dr. LOBA",
    officine: "Pharmacie Magnificat",
    city: "Abobo, Abidjan",
    photo: LOBA_PHOTO,
    module: "PHARMA PÉRIMÉS",
    title: "L'IA qui a sauvé 420 000 FCFA en deux alertes.",
    summary: "L'intelligence prédictive qui détecte les péremptions avant qu'elles ne coûtent.",
    kpis: [
      { value: "2", label: "Péremptions majeures évitées", detail: "paracétamol enfant" },
      { value: "−420k", label: "FCFA de pertes évitées", detail: "rien qu'en 2 alertes" },
      { value: "−60%", label: "Pertes totales", detail: "sur le trimestre" },
    ],
    problem: "Pharmacie de forte affluence à Abobo, la Magnificat gérait un stock de 3 200 références médicamenteuses. Les péremptions représentaient en moyenne 1,2% du chiffre d'affaires mensuel — un coût invisible mais constant que personne ne documentait clairement.",
    solution: "Activation de PHARMA PÉRIMÉS en avril 2026. Le module a analysé 18 mois d'historique de vente pour calibrer son IA prédictive, puis a émis des alertes J-90 / J-60 / J-30 sur les références à risque, avec suggestion automatique de commandes ajustées.",
    results: "En deux mois, deux alertes critiques ont évité des péremptions massives (paracétamol pédiatrique et un antibiotique peu rotatif). Les commandes suggérées ont réduit les sur-stocks de 22%. Sur le trimestre, les pertes par péremption ont chuté de 60%.",
    quote: "L'IA nous a évité deux péremptions majeures sur le paracétamol enfant. C'est devenu un outil de décision quotidien. Je n'imagine plus gérer mon stock sans le tableau de bord prédictif.",
  },
];

export function CaseStudiesPage() {
  return (
    <Shell active="Cas clients" title="Cas clients | CMES Pharma Intel — Résultats mesurés sur le terrain"
      desc="Découvrez comment les pharmaciens d'Abidjan ont transformé leur officine avec CMES Pharma Intel. KPIs réels, témoignages authentiques, résultats chiffrés."
      canonical="/cas-clients">

      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 bg-[#F9F8F6]" data-testid="cases-hero">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Études de cas</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-[92px] leading-[0.95] tracking-[-0.02em] text-[#0A0A0A] mb-6 max-w-5xl">
            Des résultats <em className="italic font-light text-[#025946]">qui parlent.</em>
          </h1>
          <p className="max-w-2xl text-[17px] text-[#4A4D4C] font-light leading-relaxed">
            Pas de promesses. Des chiffres. Des pharmaciens d'Abidjan qui ont accepté de partager leurs KPIs avant/après CMES Pharma Intel.
          </p>
        </div>
      </section>

      {CASES.map((c, i) => (
        <section key={c.id} className={`relative py-20 lg:py-28 ${i % 2 === 0 ? "bg-white border-y border-[#E5E4E2]" : "bg-[#F9F8F6]"}`} data-testid={`case-${c.id}`}>
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className={`grid grid-cols-12 gap-8 lg:gap-16 items-start ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}>
              <div className={`col-span-12 lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="sticky top-28">
                  <img src={c.photo} alt={c.pharmacist} className="w-full aspect-[4/5] object-cover grayscale" style={{ objectPosition: "center 25%" }} loading="lazy" />
                  <div className="mt-6 pt-6 border-t border-[#0A0A0A]">
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-2">{c.module}</p>
                    <h3 className="font-serif text-3xl font-light text-[#0A0A0A] mb-1">{c.pharmacist}</h3>
                    <p className="text-[14px] text-[#0A0A0A] font-semibold">{c.officine}</p>
                    <p className="text-[12px] text-[#4A4D4C] flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {c.city}</p>
                  </div>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-7">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E] mb-5">Cas client #{i + 1}</p>
                <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A] mb-6">{c.title}</h2>
                <p className="text-[16px] text-[#4A4D4C] leading-relaxed font-light mb-10 italic">{c.summary}</p>

                <div className="grid grid-cols-3 gap-4 lg:gap-6 mb-12 py-8 border-y border-[#0A0A0A]">
                  {c.kpis.map((k, j) => (
                    <div key={j} data-testid={`kpi-${c.id}-${j}`}>
                      <p className="font-serif text-4xl lg:text-5xl font-light text-[#025946] leading-none mb-2">{k.value}</p>
                      <p className="text-[12px] font-semibold text-[#0A0A0A] mb-1">{k.label}</p>
                      <p className="text-[11px] text-[#4A4D4C]">{k.detail}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-8">
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-3">01 · Le contexte</p>
                    <p className="text-[14px] text-[#0A0A0A] leading-[1.8] font-light">{c.problem}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-3">02 · La solution</p>
                    <p className="text-[14px] text-[#0A0A0A] leading-[1.8] font-light">{c.solution}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-3">03 · Les résultats</p>
                    <p className="text-[14px] text-[#0A0A0A] leading-[1.8] font-light">{c.results}</p>
                  </div>
                </div>

                <div className="mt-12 p-8 bg-[#0A0A0A] text-white relative">
                  <Quote className="absolute top-4 left-4 w-8 h-8 text-[#C9A25E]/40" strokeWidth={1} />
                  <p className="font-serif text-xl lg:text-2xl leading-[1.4] font-light italic pl-10">« {c.quote} »</p>
                  <p className="mt-6 text-right text-[11px] uppercase tracking-wider text-[#C9A25E] font-bold">— {c.pharmacist}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      <CTABand />
    </Shell>
  );
}

// ═══════════════════════════════════════════════════════════════
// 3. BLOG PAGE + ARTICLE
// ═══════════════════════════════════════════════════════════════

const ARTICLES = [
  {
    slug: "logiciel-pharmacie-cote-divoire-5-criteres",
    category: "Stratégie officinale",
    title: "Logiciel de pharmacie en Côte d'Ivoire : 5 critères pour bien choisir",
    excerpt: "Choisir son logiciel d'officine est un investissement stratégique. Voici les 5 critères incontournables pour un pharmacien ivoirien en 2026.",
    cover: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    date: "15 avril 2026",
    readTime: "7 min",
    author: "L'équipe CMES",
    content: [
      { h: "1. L'ancrage local : indispensable", p: "Un logiciel conçu à Paris ou Casablanca ne comprend pas vos contraintes : le flux CMU, les bordereaux CNAM, les particularités de la caisse AXA Côte d'Ivoire, les modalités Mobile Money. Vérifiez que votre éditeur a des références locales documentées, et que son support comprend vos réalités quotidiennes. L'ancrage ivoirien n'est pas un détail — c'est la condition d'efficacité." },
      { h: "2. La conformité réglementaire", p: "La dispensation pharmaceutique est encadrée par des textes ivoiriens précis. Votre logiciel doit respecter les normes pharmaceutiques nationales, gérer correctement les ordonnances, tracer les délivrances réglementées (stupéfiants, antibiotiques), et protéger le secret médical. Exigez la documentation sur la gestion des données patients : chiffrement, hébergement, durée de conservation." },
      { h: "3. L'intelligence prédictive", p: "En 2026, un logiciel qui se contente de vous afficher votre stock n'est plus suffisant. L'IA doit détecter les ruptures avant qu'elles ne surviennent, alerter sur les péremptions 90/60/30 jours à l'avance, suggérer des commandes ajustées à vos patterns de vente. L'IA n'est plus un luxe — c'est un levier de rentabilité." },
      { h: "4. Le paiement en Mobile Money", p: "Un éditeur qui ne propose pas MTN, Orange, Moov et Wave en paiement natif vous force à des gymnastiques comptables inutiles. Mobile Money doit être intégré nativement à votre logiciel, pour vous comme pour vos clients. Vérifiez aussi les frais cachés : certains éditeurs facturent 1,5 à 3% de commission — à éviter absolument." },
      { h: "5. Le support humain et local", p: "Un bug survient un samedi à 17h. Qui répond ? Si la réponse est « un ticket sera traité sous 72h », fuyez. Un support WhatsApp 7j/7 en français, des formateurs formés localement, une présence physique à Abidjan sont les garanties d'un véritable partenaire. Votre logiciel n'est pas un fichier — c'est une relation humaine." },
    ],
    conclusion: "Chez CMES Pharma Intel, chacun de ces critères est un pilier de notre proposition. Ancrage ivoirien, conformité, IA, Mobile Money, support 7j/7 : notre engagement est total. Testez gratuitement pendant 30 jours.",
  },
  {
    slug: "reduire-pertes-peremption-60-pourcent",
    category: "Gestion des stocks",
    title: "Comment réduire de 60% les pertes par péremption en officine",
    excerpt: "Les péremptions sont la fuite silencieuse de toute officine. Voici la méthode éprouvée pour les diviser par 2,5 en trois mois.",
    cover: "https://images.unsplash.com/photo-1576602976047-174e57a47881?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    date: "28 mars 2026",
    readTime: "6 min",
    author: "L'équipe CMES",
    content: [
      { h: "Le vrai coût des péremptions (souvent sous-estimé)", p: "Une officine moyenne à Abidjan gère 3 000 à 5 000 références. Les péremptions annuelles représentent entre 1,2% et 2,5% du chiffre d'affaires — soit, pour une officine réalisant 180 millions FCFA/an, une perte nette de 2 à 4,5 millions FCFA. Cet argent n'est pas perdu sur une ligne comptable : il est jeté. Et pourtant, la plupart des pharmaciens n'en tiennent aucun registre précis." },
      { h: "Méthode 1 : La cartographie des risques", p: "La première étape est d'identifier vos 20% de références les plus à risque. Ce sont généralement : les pédiatriques (rotation lente), les spécialités saisonnières (antipaludéens hors saison), les nouveautés à faible rotation, les produits commandés en surstock lors d'une promotion fournisseur. Un logiciel comme PHARMA PÉRIMÉS cartographie automatiquement ces zones à risque et vous alerte." },
      { h: "Méthode 2 : Les alertes à 3 étages", p: "J-90 : vous avez encore le temps d'organiser un déstockage ou une promotion. J-60 : vous devez agir. J-30 : c'est le dernier recours (transfert inter-officines, retour fournisseur quand possible). Sans alertes automatiques, vous découvrez le problème au comptoir — trop tard. Avec des alertes programmées, vous anticipez." },
      { h: "Méthode 3 : Les commandes suggérées intelligentes", p: "Une IA analyse vos 18 derniers mois de ventes, détecte les saisonnalités (antipaludéens en hivernage, antigrippaux en saison sèche), et ajuste automatiquement les volumes à commander. Fini les sur-stocks émotionnels en fin de promotion commerciale." },
      { h: "Méthode 4 : Le déstockage éthique", p: "Quand une péremption approche, la politique doit être claire : promotion ciblée (−30% à J-90), transfert vers une officine sœur, don à une ONG médicale (déductible fiscalement), ou destruction documentée. Chacune de ces options doit être tracée dans votre ERP pour audit." },
    ],
    conclusion: "Avec PHARMA PÉRIMÉS, les officines partenaires réduisent leurs pertes par péremption de 60% en trois mois. Ce n'est pas une promesse — c'est une mesure. Demandez une démo gratuite pour voir comment votre stock serait analysé.",
  },
  {
    slug: "recouvrement-cmu-cnam-3-pieges",
    category: "Finances officinales",
    title: "Recouvrement CMU/CNAM : les 3 pièges à éviter",
    excerpt: "Le tiers-payant CMU/CNAM représente 30 à 45% du chiffre d'affaires d'une officine ivoirienne. Trois pièges classiques plombent votre trésorerie.",
    cover: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    date: "12 mars 2026",
    readTime: "5 min",
    author: "L'équipe CMES",
    content: [
      { h: "Piège n°1 : Le dépôt sans cachet", p: "Un bordereau CMU non cacheté au dépôt = un bordereau contestable = un risque de rejet 30 à 45 jours plus tard. Pourtant, nombre de pharmaciens se contentent de déposer leurs bordereaux sans photographier le cachet de réception. La preuve du dépôt se perd, et les assureurs en profitent. Règle absolue : chaque dépôt doit être photographié avec le cachet de l'assureur visible, archivé numériquement." },
      { h: "Piège n°2 : L'absence de suivi systématique", p: "Beaucoup d'officines suivent leurs bordereaux dans des cahiers manuscrits ou des fichiers Excel. Résultat : les bordereaux « oubliés » s'accumulent, les relances se font au hasard, et certaines créances se perdent purement et simplement. Un pipeline digitalisé (déposé → en cours → payé) avec relances automatiques est indispensable." },
      { h: "Piège n°3 : La non-réconciliation avec les paiements", p: "Quand un virement arrive de la CMU, savez-vous précisément à quels bordereaux il correspond ? Beaucoup d'officines ne peuvent pas le dire. Résultat : certaines factures sont considérées comme payées alors qu'elles ne le sont pas (l'assureur ayant regroupé plusieurs paiements), et vice-versa. La réconciliation automatique bordereaux ↔ virements est la clé d'une trésorerie saine." },
      { h: "La solution : un pipeline digitalisé de bout en bout", p: "Scan OCR au dépôt (cachet obligatoire) → suivi statut en temps réel → relance auto à J+7 / J+15 / J+30 → réconciliation automatique virement/bordereau → alerte en cas d'anomalie. C'est exactement ce que PHARMA ASSURANCES propose. Résultat : des délais divisés par 10, une trésorerie débloquée, et la paix mentale retrouvée." },
    ],
    conclusion: "Le recouvrement n'est pas une fatalité. C'est un système qu'il faut construire. CMES Pharma Intel vous accompagne dans cette transformation, avec un essai gratuit de 30 jours.",
  },
];

export function BlogPage() {
  return (
    <Shell active="Blog" title="Blog CMES Pharma Intel | Conseils, stratégies & innovations pour pharmaciens"
      desc="Articles d'experts pour pharmaciens ivoiriens : gestion des stocks, recouvrement CMU/CNAM, IA pharmaceutique, conformité réglementaire. Mis à jour mensuellement."
      canonical="/blog">
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-[#F9F8F6]" data-testid="blog-hero">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Le Journal CMES</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-[92px] leading-[0.95] tracking-[-0.02em] text-[#0A0A0A] mb-6 max-w-5xl">
            Des idées <em className="italic font-light text-[#025946]">pour votre officine.</em>
          </h1>
          <p className="max-w-2xl text-[17px] text-[#4A4D4C] font-light leading-relaxed">
            Analyses, méthodes, retours d'expérience. Un article par mois, zéro bruit, que de la valeur.
          </p>
        </div>
      </section>

      <section className="relative bg-[#F9F8F6] pb-28" data-testid="blog-list">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {ARTICLES.map((a, i) => (
              <motion.article key={a.slug}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`blog-card-${a.slug}`}>
                <Link to={`/blog/${a.slug}`} className="group block border border-[#E5E4E2] bg-white hover:border-[#025946] transition-all duration-500 overflow-hidden h-full flex flex-col">
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#0A0A0A]">
                    <img src={a.cover} alt={a.title} className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 opacity-80 group-hover:opacity-100 scale-105 group-hover:scale-100 transition-[filter,opacity,transform] duration-700" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#025946]/40 via-transparent to-transparent"></div>
                    <div className="absolute top-4 left-4 bg-[#C9A25E] text-[#0A0A0A] px-3 py-1 text-[10px] font-bold uppercase tracking-wider">{a.category}</div>
                  </div>
                  <div className="p-6 lg:p-8 flex flex-col flex-1">
                    <div className="flex items-center gap-4 text-[11px] text-[#4A4D4C] uppercase tracking-wider mb-4">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {a.date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {a.readTime}</span>
                    </div>
                    <h2 className="font-serif text-2xl lg:text-3xl font-light text-[#0A0A0A] mb-4 leading-tight group-hover:text-[#025946] transition-colors">{a.title}</h2>
                    <p className="text-[13px] text-[#4A4D4C] leading-relaxed font-light mb-6 flex-1">{a.excerpt}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-[#E5E4E2] group-hover:border-[#025946] transition-colors">
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-[#0A0A0A]">Lire l'article</span>
                      <ArrowUpRight className="w-4 h-4 text-[#025946] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </Shell>
  );
}

export function BlogArticlePage() {
  const { slug } = useParams();
  const article = ARTICLES.find(a => a.slug === slug);
  if (!article) return <Shell active="Blog" title="Article introuvable" desc="" canonical="/blog"><div className="pt-40 pb-40 text-center"><p className="text-2xl">Article introuvable</p><Link to="/blog" className="mt-6 inline-block text-[#025946]">← Retour au blog</Link></div></Shell>;

  return (
    <Shell active="Blog" title={`${article.title} | Blog CMES Pharma Intel`} desc={article.excerpt} canonical={`/blog/${article.slug}`}>
      <article className="pt-32 pb-20 bg-[#F9F8F6]" data-testid="blog-article">
        <div className="max-w-[860px] mx-auto px-6 lg:px-12">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-wider text-[#025946] font-semibold mb-10 hover:gap-3 transition-all" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" /> Retour au blog
          </Link>
          <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E] mb-5">{article.category}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-[-0.02em] text-[#0A0A0A] mb-8">{article.title}</h1>
          <div className="flex items-center gap-6 text-[12px] text-[#4A4D4C] uppercase tracking-wider pb-8 border-b border-[#E5E4E2]">
            <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {article.date}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {article.readTime}</span>
            <span>— {article.author}</span>
          </div>
        </div>
        <div className="max-w-[980px] mx-auto px-6 lg:px-12 mt-10 mb-14">
          <img src={article.cover} alt={article.title} className="w-full aspect-[16/9] object-cover" loading="lazy" />
        </div>
        <div className="max-w-[740px] mx-auto px-6 lg:px-12 space-y-10">
          <p className="font-serif text-xl lg:text-2xl italic text-[#4A4D4C] leading-[1.6] font-light border-l-2 border-[#025946] pl-6">{article.excerpt}</p>
          {article.content.map((s, i) => (
            <div key={i}>
              <h2 className="font-serif text-3xl lg:text-4xl font-light text-[#0A0A0A] mb-4">{s.h}</h2>
              <p className="text-[16px] text-[#0A0A0A] leading-[1.8] font-light">{s.p}</p>
            </div>
          ))}
          <div className="mt-14 p-8 lg:p-10 bg-[#0A0A0A] text-white">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E] mb-4">En résumé</p>
            <p className="font-serif text-xl lg:text-2xl font-light leading-[1.4]">{article.conclusion}</p>
            <Link to="/register" className="mt-8 inline-flex items-center gap-2 bg-[#C9A25E] hover:bg-[#D4B06E] text-[#0A0A0A] px-6 py-3 text-[13px] font-bold uppercase tracking-wider transition-colors">
              Commencer l'essai 30 jours <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </article>
    </Shell>
  );
}

// ═══════════════════════════════════════════════════════════════
// 4. CONTACT PAGE
// ═══════════════════════════════════════════════════════════════

export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", pharmacy: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND}/api/public/contact`, form);
      setSent(true);
      toast.success("Message envoyé. Nous vous recontactons sous 24 h.");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Une erreur est survenue. Vérifiez vos informations.");
    } finally { setLoading(false); }
  };

  return (
    <Shell active="Contact" title="Contactez CMES Pharma Intel | WhatsApp 7j/7 · Abidjan"
      desc="Une question, une démo, un devis réseau ? Contactez-nous via WhatsApp, email ou le formulaire. Réponse sous 24h garantie."
      canonical="/contact">
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#F9F8F6]" data-testid="contact-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-12 gap-8 lg:gap-16">
            <div className="col-span-12 lg:col-span-5">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Parlons-nous</p>
              <h1 className="font-serif text-5xl lg:text-7xl leading-[0.98] tracking-[-0.02em] text-[#0A0A0A] mb-6">
                Une <em className="italic font-light text-[#025946]">conversation</em>, pas un ticket.
              </h1>
              <p className="text-[15px] text-[#4A4D4C] leading-[1.7] font-light mb-10">
                Nous vous répondons sous 24 h ouvrées. Pour les questions urgentes, privilégiez WhatsApp — nous sommes disponibles 7j/7.
              </p>
              <div className="space-y-6">
                <a href="https://wa.me/2250768127000" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp"
                  className="flex items-center gap-4 p-5 bg-white border border-[#E5E4E2] hover:border-[#025946] transition-all group">
                  <div className="w-12 h-12 rounded-full bg-[#025946] flex items-center justify-center"><MessageCircle className="w-5 h-5 text-white" /></div>
                  <div className="flex-1">
                    <p className="text-[11px] uppercase tracking-wider text-[#4A4D4C] mb-1">WhatsApp · le plus rapide</p>
                    <p className="text-[15px] font-semibold text-[#0A0A0A] group-hover:text-[#025946] transition-colors">+225 07 68 12 70 00</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[#025946]" />
                </a>
                <a href="mailto:innovationcmes@gmail.com" data-testid="contact-email"
                  className="flex items-center gap-4 p-5 bg-white border border-[#E5E4E2] hover:border-[#025946] transition-all group">
                  <div className="w-12 h-12 rounded-full bg-[#0A0A0A] flex items-center justify-center"><Mail className="w-5 h-5 text-white" /></div>
                  <div className="flex-1">
                    <p className="text-[11px] uppercase tracking-wider text-[#4A4D4C] mb-1">Email</p>
                    <p className="text-[15px] font-semibold text-[#0A0A0A] group-hover:text-[#025946] transition-colors">innovationcmes@gmail.com</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[#025946]" />
                </a>
                <div className="flex items-center gap-4 p-5 bg-white border border-[#E5E4E2]">
                  <div className="w-12 h-12 rounded-full bg-[#C9A25E] flex items-center justify-center"><MapPin className="w-5 h-5 text-[#0A0A0A]" /></div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-[#4A4D4C] mb-1">Siège</p>
                    <p className="text-[15px] font-semibold text-[#0A0A0A]">Abidjan, Côte d'Ivoire</p>
                    <p className="text-[12px] text-[#4A4D4C]">Lun-Dim · 7j/7</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-7">
              <div className="bg-white border border-[#E5E4E2] p-8 lg:p-12">
                {sent ? (
                  <div className="text-center py-16" data-testid="contact-success">
                    <div className="w-16 h-16 rounded-full bg-[#025946] flex items-center justify-center mx-auto mb-6"><BadgeCheck className="w-8 h-8 text-white" /></div>
                    <h2 className="font-serif text-3xl font-light text-[#0A0A0A] mb-3">Message reçu.</h2>
                    <p className="text-[14px] text-[#4A4D4C] max-w-md mx-auto leading-relaxed">Nous vous recontactons sous 24 heures ouvrées. Pour les urgences, n'hésitez pas à nous joindre sur WhatsApp.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-4">─── Formulaire</p>
                    <h2 className="font-serif text-3xl lg:text-4xl font-light text-[#0A0A0A] mb-6">Dites-nous en plus.</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-[#4A4D4C] font-semibold mb-2">Nom complet *</label>
                        <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className="w-full px-4 py-3 border border-[#E5E4E2] bg-[#F9F8F6] focus:border-[#025946] focus:outline-none text-[14px] transition-colors" data-testid="form-name" />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-[#4A4D4C] font-semibold mb-2">Email professionnel *</label>
                        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full px-4 py-3 border border-[#E5E4E2] bg-[#F9F8F6] focus:border-[#025946] focus:outline-none text-[14px] transition-colors" data-testid="form-email" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-[#4A4D4C] font-semibold mb-2">Officine</label>
                        <input type="text" value={form.pharmacy} onChange={(e) => setForm({ ...form, pharmacy: e.target.value })}
                          placeholder="Ex: Pharmacie de la Plateau"
                          className="w-full px-4 py-3 border border-[#E5E4E2] bg-[#F9F8F6] focus:border-[#025946] focus:outline-none text-[14px] transition-colors" data-testid="form-pharmacy" />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-[#4A4D4C] font-semibold mb-2">Téléphone</label>
                        <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="+225 ..."
                          className="w-full px-4 py-3 border border-[#E5E4E2] bg-[#F9F8F6] focus:border-[#025946] focus:outline-none text-[14px] transition-colors" data-testid="form-phone" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-[#4A4D4C] font-semibold mb-2">Votre message *</label>
                      <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Dites-nous ce dont vous avez besoin — démo, devis, question, suggestion..."
                        className="w-full px-4 py-3 border border-[#E5E4E2] bg-[#F9F8F6] focus:border-[#025946] focus:outline-none text-[14px] transition-colors resize-none" data-testid="form-message" />
                    </div>
                    <p className="text-[11px] text-[#4A4D4C]/70">Vos données sont protégées. Utilisées uniquement pour vous recontacter. Aucun partage à des tiers.</p>
                    <button type="submit" disabled={loading} data-testid="form-submit"
                      className="w-full py-4 bg-[#0A0A0A] hover:bg-[#025946] text-white text-[13px] font-semibold uppercase tracking-wider flex items-center justify-center gap-3 transition-colors disabled:opacity-60">
                      {loading ? "Envoi..." : <>Envoyer le message <Send className="w-4 h-4" /></>}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Shell>
  );
}

// ═══════════════════════════════════════════════════════════════
// SHARED CTA BAND
// ═══════════════════════════════════════════════════════════════
function CTABand() {
  const nav = useNavigate();
  return (
    <section className="relative bg-[#F9F8F6] py-20 lg:py-24" data-testid="cta-band">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="border-t border-b border-[#0A0A0A] py-14 lg:py-16 px-6 lg:px-12 relative">
          <div className="absolute top-0 left-0 w-4 h-4 border-l border-t border-[#025946] -translate-x-[1px] -translate-y-[1px]"></div>
          <div className="absolute top-0 right-0 w-4 h-4 border-r border-t border-[#025946] translate-x-[1px] -translate-y-[1px]"></div>
          <div className="absolute bottom-0 left-0 w-4 h-4 border-l border-b border-[#025946] -translate-x-[1px] translate-y-[1px]"></div>
          <div className="absolute bottom-0 right-0 w-4 h-4 border-r border-b border-[#025946] translate-x-[1px] translate-y-[1px]"></div>
          <div className="grid grid-cols-12 gap-6 items-center">
            <div className="col-span-12 lg:col-span-8">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-4">─── Essai gratuit</p>
              <h2 className="font-serif text-3xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0A0A] mb-3">
                30 jours. <em className="italic font-light">Aucun engagement.</em>
              </h2>
              <p className="text-[14px] text-[#4A4D4C] leading-relaxed font-light max-w-xl">Rejoignez les officines qui prennent une longueur d'avance. Sans carte bancaire. Satisfait ou remboursé.</p>
            </div>
            <div className="col-span-12 lg:col-span-4 flex flex-col gap-3 lg:items-end">
              <button onClick={() => nav("/register")} data-testid="cta-band-register"
                className="px-8 py-4 bg-[#0A0A0A] hover:bg-[#025946] text-white text-[13px] font-semibold flex items-center justify-center gap-3 transition-all">
                Commencer l'essai <ArrowUpRight className="w-5 h-5" />
              </button>
              <Link to="/tarifs" data-testid="cta-band-pricing" className="px-8 py-4 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[13px] font-semibold flex items-center justify-center gap-3 transition-all">
                Voir les tarifs
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
